// Per-type column resolver for the /assets tree (spec S1).
//
// Two columns ship today: the address (for the types that carry one) and the
// External ID. Both come off the assets list endpoint without a per-row fetch
// — see backend's ?include=external_ids and ?include=properties.
//
// TODO(s1-columns): the spec also asks for area / asset class / brand+model /
// power-or-flow per type. Those are property values too, so they can be read
// off `asset.properties` the same way `address` is once we agree on the codes
// and the row has the width for them.

import { formatIso20022AddressShort } from '../lib/format-address'

export interface AssetRowColumn {
  /** Stable key for React lists. */
  key: string
  /** Optional short label rendered above the value (kept null today —
   *  columns are denser without inline labels and the spec calls for
   *  vertically-aligned values). */
  label?: string
  /** Display value, or `null` for an empty cell (no `—` fallback). */
  value: string | null
  /** Fixed column width. Every row emits every column — an empty cell still
   *  reserves its width — so values line up vertically down the tree. */
  widthPx: number
  /** Gutter before the column, in centimetres. */
  gapCm?: number
  /** Values that read better in a monospace face (identifiers). */
  mono?: boolean
}

interface AssetInlineProperty {
  code?: string
  value?: unknown
}

interface AssetLite {
  external_ids?: { code?: string } & Record<string, unknown>
  properties?: AssetInlineProperty[]
  name?: string
  asset_type_uid?: string
}

/** Asset types whose rows carry an address of their own. Sites hold the
 *  address of the group, buildings/houses/parcels their own. */
export const ADDRESS_ASSET_TYPE_CODES = new Set([
  'building_group',
  'building',
  'house',
  'parcel_land',
])

const noop: AssetRowColumn[] = []

function readAddress(asset: AssetLite): string | null {
  const props = asset.properties
  if (!props?.length) return null

  const iso = props.find(p => p.code === 'address_iso20022')?.value
  if (iso && typeof iso === 'object') {
    const formatted = formatIso20022AddressShort(iso as Record<string, unknown>)
    if (formatted) return formatted
  }

  const raw = props.find(p => p.code === 'address_raw')?.value
  return typeof raw === 'string' && raw.trim() ? raw : null
}

/** Address column width. Long addresses truncate; the full value is on the
 *  row's `title`. */
export const ADDRESS_COL_PX = 260

// TODO(s1-columns): per-type columns beyond these two — area / asset class /
// brand+model / power-or-flow — slot in between address and external ID, and
// need `assetTypeCode` to pick which. Floor number and lot number likewise.
export function getAssetRowColumns(asset: AssetLite, assetTypeCode: string | undefined): AssetRowColumn[] {
  if (!asset) return noop

  const externalCode =
    typeof asset.external_ids?.code === 'string' ? (asset.external_ids.code as string) : null

  const address =
    assetTypeCode && ADDRESS_ASSET_TYPE_CODES.has(assetTypeCode) ? readAddress(asset) : null

  // Every row emits both columns, empty or not: a floor with no address and no
  // external ID still reserves the slots, which is what keeps the codes of the
  // rows above and below it in one straight column.
  return [
    { key: 'address', value: address, widthPx: ADDRESS_COL_PX },
    { key: 'external_id', value: externalCode, widthPx: 96, gapCm: 2, mono: true },
  ]
}
